import { Controller } from '@hotwired/stimulus';
import LocationService from '../services/location_service.js';

// Map-specific constants
const MAP_CONFIG = {
  DEFAULT_CENTER: { lat: -36.8485, lng: 174.7633 }, // Auckland
  DEFAULT_ZOOM: 10,
  DETAILED_ZOOM: 14,
  GEOLOCATION_TIMEOUT: 5000,
  CACHE_DURATION_MS: 300000, // 5 minutes
  CIRCLE_STYLES: {
    strokeColor: '#0d6efd',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#0d6efd',
    fillOpacity: 0.15,
  },
};

const MESSAGES = {
  MAPS_NOT_LOADED: 'Google Maps API not loaded yet',
  NO_GEOLOCATION: 'Your browser does not support geolocation',
  NO_LOCATION_DATA: 'No location data for selected place',
  PERMISSION_DENIED: 'Location access denied. Please enable location permissions.',
  POSITION_UNAVAILABLE: 'Location information unavailable.',
  TIMEOUT: 'Location request timed out.',
  DEFAULT_ERROR: 'Unable to get your location',
  GETTING_ADDRESS: 'Getting your address...',
  USE_CURRENT_LOCATION: 'Use my current location',
  CURRENT_LOCATION: 'Your current location',
};

export default class MapLocationPickerController extends Controller {

  connect() {
    this.state = {
      map: null,
      autocomplete: null,
      marker: null,
      radiusCircle: null,
      selectedLocation: null,
      selectedRadius: 25,
    };
    
    this.initialize();
  }

  disconnect() {
    this.destroyMap();
  }

  // Initialization Methods
  initialize() {
    // Wait for Google Maps to be available
    this.waitForGoogleMaps().then(() => {
      this.initializeMap();
      this.initializeAutocomplete();
      this.loadInitialLocation();

      if (this.loadFromUrlValue) {
        this.loadLocationFromURL();
      }
    }).catch((error) => {
      console.error('Failed to initialize Google Maps:', error);
    });
  }

  waitForGoogleMaps() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait time
      
      const checkMaps = () => {
        if (this.isGoogleMapsLoaded()) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkMaps, 100);
        } else {
          reject(new Error('Google Maps failed to load within timeout'));
        }
      };
      
      checkMaps();
    });
  }

  isGoogleMapsLoaded() {
    return typeof google !== 'undefined' && google.maps;
  }

  initializeMap() {
    if (!this.hasMapTarget) return;

    this.state.map = new google.maps.Map(this.mapTarget, {
      center: MAP_CONFIG.DEFAULT_CENTER,
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      mapId: 'DEMO_MAP_ID',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      // Disable all map interactions - map is display-only
      gestureHandling: 'none',
      zoomControl: false,
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
    });
  }

  initializeAutocomplete() {
    if (!this.hasAddressInputTarget) return;

    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ['nz'],
    });

    placeAutocomplete.style.colorScheme = 'light';
    placeAutocomplete.classList.add('flex-grow-1');
    placeAutocomplete.Dg?.setAttribute('placeholder', 'Search by town/city, suburb, postcode or street address');
    this.addressInputTarget.replaceWith(placeAutocomplete);
    
    placeAutocomplete.addEventListener('gmp-select', this.handlePlaceSelection.bind(this));
  }

  destroyMap() {
    if (this.state.marker) {
      this.state.marker.setMap(null);
    }
    if (this.state.radiusCircle) {
      this.state.radiusCircle.setMap(null);
    }
    this.state = null;
  }

  // Stimulus Actions
  getCurrentLocation() {
    if (!navigator.geolocation) {
      alert(MESSAGES.NO_GEOLOCATION);
      return;
    }

    this.setLocationButtonState(true, MESSAGES.GETTING_ADDRESS);

    navigator.geolocation.getCurrentPosition(
      this.handleGeolocationSuccess.bind(this),
      this.handleGeolocationError.bind(this),
      this.getGeolocationOptions(),
    );
  }

  updateRadius(event) {
    this.state.selectedRadius = parseInt(event.target.value);
    if (this.hasRadiusValueTarget) {
      this.radiusValueTarget.textContent = `${this.state.selectedRadius} km`;
    }
    this.updateRadiusCircle();
  }

  confirmLocation() {
    if (!this.state.selectedLocation) return;

    const locationData = {
      lat: this.state.selectedLocation.lat,
      lng: this.state.selectedLocation.lng,
      address: this.state.selectedLocation.address,
      radius: this.state.selectedRadius,
    };

    const newURL = LocationService.updateURL(locationData);
    if (newURL) {
      window.location.href = newURL;
    }
  }

  // Modal Events
  modalShown() {
    if (!this.state.map) return;
    
    google.maps.event.trigger(this.state.map, 'resize');
    
    if (this.state.selectedLocation) {
      this.centerMapOnLocation(
        this.state.selectedLocation.lat,
        this.state.selectedLocation.lng,
      );
    } else {
      this.getCurrentLocationSilently();
    }
  }

  // Event Handlers
  getCurrentLocationSilently() {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.centerMapOnLocation(
          position.coords.latitude,
          position.coords.longitude,
          MAP_CONFIG.DETAILED_ZOOM,
        );
      },
      () => this.state.map.setCenter(MAP_CONFIG.DEFAULT_CENTER),
      this.getGeolocationOptions(true),
    );
  }

  async handlePlaceSelection({ placePrediction }) {
    const place = placePrediction.toPlace();
    await place.fetchFields({ 
      fields: ['displayName', 'formattedAddress', 'location'], 
    });

    if (!place.location) {
      console.warn(MESSAGES.NO_LOCATION_DATA);
      return;
    }

    const lat = place.location.lat();
    const lng = place.location.lng();
    const address = place.formattedAddress || place.displayName;
    
    this.selectLocation(lat, lng, address);
    this.centerMapOnLocation(lat, lng, MAP_CONFIG.DEFAULT_ZOOM);
  }

  handleGeolocationSuccess(position) {
    const { latitude: lat, longitude: lng } = position.coords;
    
    this.reverseGeocodeAndSelect(lat, lng, () => {
      this.setLocationButtonState(false, MESSAGES.USE_CURRENT_LOCATION);
    });
    
    this.centerMapOnLocation(lat, lng, MAP_CONFIG.DETAILED_ZOOM);
  }

  handleGeolocationError(error) {
    const message = this.getGeolocationErrorMessage(error);
    alert(message);
    this.setLocationButtonState(false, MESSAGES.USE_CURRENT_LOCATION);
  }

  // Location Methods
  selectLocation(lat, lng, address = null) {
    this.state.selectedLocation = { lat, lng, address };
    
    const locationData = {
      lat,
      lng,
      address,
      radius: this.state.selectedRadius,
    };
    
    LocationService.saveToStorage(locationData);
    this.updateMarker(lat, lng, address);
    this.updateRadiusCircle();
    this.updateLocationInfo(address || LocationService.formatCoordinates(lat, lng));
    this.enableConfirmButton();
    this.notifyLocationDisplays();
    LocationService.dispatchLocationChange(locationData);
  }

  // Map Manipulation Methods
  updateMarker(lat, lng, address) {
    if (this.state.marker) {
      this.state.marker.setMap(null);
    }

    if (!google.maps.marker?.AdvancedMarkerElement) {
      throw new Error('AdvancedMarkerElement not available');
    }

    this.state.marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat, lng },
      map: this.state.map,
      title: address || MESSAGES.CURRENT_LOCATION,
    });
  }

  updateRadiusCircle() {
    if (!this.state.selectedLocation) return;

    if (this.state.radiusCircle) {
      this.state.radiusCircle.setMap(null);
    }

    this.state.radiusCircle = new google.maps.Circle({
      ...MAP_CONFIG.CIRCLE_STYLES,
      map: this.state.map,
      center: this.state.selectedLocation,
      radius: this.state.selectedRadius * 1000,
    });

    this.state.map.fitBounds(this.state.radiusCircle.getBounds());
  }

  centerMapOnLocation(lat, lng, zoom = null) {
    this.state.map.setCenter(new google.maps.LatLng(lat, lng));
    if (zoom) {
      this.state.map.setZoom(zoom);
    }
  }

  // Geocoding Methods
  reverseGeocodeAndSelect(lat, lng, callback = null) {
    const geocoder = new google.maps.Geocoder();
    
    this.selectLocation(lat, lng, MESSAGES.CURRENT_LOCATION);

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        this.selectLocation(lat, lng, results[0].formatted_address);
      } else {
        console.warn('Reverse geocoding failed:', status);
      }
      
      if (callback) callback();
    });
  }

  // UI Update Methods
  updateLocationInfo(address) {
    if (!this.hasLocationInfoTarget) return;
    if (!this.state.selectedLocation) return;

    if (this.hasLocationAddressTarget) {
      this.locationAddressTarget.textContent = address;
    }
    
    if (this.hasLocationCoordsTarget) {
      this.locationCoordsTarget.textContent = LocationService.formatCoordinates(
        this.state.selectedLocation.lat,
        this.state.selectedLocation.lng,
      );
    }
    
    this.locationInfoTarget.classList.remove('d-none');
  }

  setLocationButtonState(disabled, text) {
    if (this.hasCurrentLocationBtnTarget) {
      this.currentLocationBtnTarget.disabled = disabled;
    }
    if (this.hasLocationBtnTextTarget) {
      this.locationBtnTextTarget.textContent = text;
    }
  }

  enableConfirmButton() {
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = false;
    }
  }

  // Notify connected location display controllers
  notifyLocationDisplays() {
    this.locationDisplayOutlets.forEach(outlet => {
      if (outlet.updateDisplay) {
        outlet.updateDisplay();
      }
    });
  }

  // Location Confirmation
  clearRegionParams(params) {
    params.delete('menu[region]');
    params.delete('menu[district]');
  }

  setLocationParams(params) {
    const { selectedLocation, selectedRadius } = this.state;
    
    params.set('location[lat]', selectedLocation.lat.toString());
    params.set('location[lng]', selectedLocation.lng.toString());
    params.set('location[radius]', selectedRadius.toString());
    
    if (selectedLocation.address) {
      params.set('location[address]', selectedLocation.address);
    }
  }

  // Storage Methods
  saveLocationToStorage() {
    if (!this.state.selectedLocation) return;

    const locationData = {
      ...this.state.selectedLocation,
      radius: this.state.selectedRadius,
      timestamp: Date.now(),
    };

    localStorage.setItem('supergold_selected_location', JSON.stringify(locationData));
  }

  loadLocationFromStorage() {
    const locationData = LocationService.loadFromStorage();
    if (locationData) {
      this.updateRadiusFromStorage(locationData.radius);
      return {
        lat: locationData.lat,
        lng: locationData.lng,
        address: locationData.address,
      };
    }
    return null;
  }

  updateRadiusFromStorage(radius) {
    if (!radius) return;
    this.updateRadiusUI(radius);
  }

  updateRadiusUI(radius) {
    if (!radius) return;

    this.state.selectedRadius = radius;
    
    // Update the slider value
    if (this.hasRadiusSelectTarget) {
      this.radiusSelectTarget.value = radius.toString();
    }
    
    // Update the display text
    if (this.hasRadiusValueTarget) {
      this.radiusValueTarget.textContent = `${radius} km`;
    }
  }

  // URL Parameter Methods
  loadLocationFromURL() {
    const params = new URLSearchParams(window.location.search);
    const location = LocationService.parseLocationParams(params);
    
    if (!location) return;

    this.state.selectedLocation = location;
    
    if (location.radius) {
      this.updateRadiusFromURL(location.radius);
    }

    this.selectLocation(location.lat, location.lng, location.address);
  }

  updateRadiusFromURL(radius) {
    this.state.selectedRadius = radius;
    
    if (this.hasRadiusSelectTarget) {
      this.radiusSelectTarget.value = radius.toString();
    }
    if (this.hasRadiusValueTarget) {
      this.radiusValueTarget.textContent = `${radius} km`;
    }
  }

  // Initial Load Methods
  loadInitialLocation() {
    const storedLocation = this.loadLocationFromStorage();
    
    if (storedLocation) {
      this.handleStoredLocation(storedLocation);
    } else {
      this.getInitialGeolocation();
    }
  }

  handleStoredLocation(location) {
    this.selectLocation(location.lat, location.lng, location.address);
    this.centerMapOnLocation(location.lat, location.lng, MAP_CONFIG.DETAILED_ZOOM);
    
    // Update radius UI elements to reflect stored radius
    if (location.radius) {
      this.updateRadiusUI(location.radius);
    }
  }

  getInitialGeolocation() {
    if (!navigator.geolocation) {
      this.state.map.setCenter(MAP_CONFIG.DEFAULT_CENTER);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        this.reverseGeocodeAndSelect(lat, lng);
        this.centerMapOnLocation(lat, lng, MAP_CONFIG.DETAILED_ZOOM);
      },
      () => this.state.map.setCenter(MAP_CONFIG.DEFAULT_CENTER),
      this.getGeolocationOptions(true),
    );
  }

  // Helper Methods
  getGeolocationErrorMessage(error) {
    switch (error.code) {
    case error.PERMISSION_DENIED:
      return MESSAGES.PERMISSION_DENIED;
    case error.POSITION_UNAVAILABLE:
      return MESSAGES.POSITION_UNAVAILABLE;
    case error.TIMEOUT:
      return MESSAGES.TIMEOUT;
    default:
      return MESSAGES.DEFAULT_ERROR;
    }
  }

  getGeolocationOptions(cached = false) {
    return {
      enableHighAccuracy: true,
      timeout: cached ? 10000 : MAP_CONFIG.GEOLOCATION_TIMEOUT,
      maximumAge: cached ? MAP_CONFIG.CACHE_DURATION_MS : 0,
    };
  }


  // Public API for other controllers
  getCurrentLocationData() {
    return this.state.selectedLocation;
  }

  getCurrentRadius() {
    return this.state.selectedRadius;
  }

  getLocationDisplayText() {
    return LocationService.getLocationDisplayText();
  }
}

// Static properties for Stimulus controller
MapLocationPickerController.targets = [
  'map',
  'addressInput',
  'currentLocationBtn',
  'locationBtnText',
  'radiusSelect',
  'radiusValue',
  'confirmBtn',
  'locationInfo',
  'locationAddress',
  'locationCoords',
];

MapLocationPickerController.outlets = ['locationDisplay'];

MapLocationPickerController.values = {
  loadFromUrl: { type: Boolean, default: true },
};